import React, { forwardRef, useState, useEffect } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  FormControlProps,
  Input,
  InputProps,
  HStack,
  Spacer,
} from '@chakra-ui/react';

export type DateInputProps = {
  helper?: string;
  error?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  value?: string;
} & Omit<FormControlProps, 'onChange'> &
  InputProps;

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(({ id, helper, error, onChange, value, ...props }) => {
  const [day, setDay] = useState<string | undefined>(undefined);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [year, setYear] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (value) {
      const [yearValue, monthValue, dayValue] = value.split('-');
      setDay(dayValue);
      setMonth(monthValue);
      setYear(yearValue);
    }
  }, [value]);

  useEffect(() => {
    if (day && month && year) {
      onChange(`${year}-${month}-${day}`);
    }
  }, [day, month, year]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.currentTarget.id) {
      case `${id}-dd`:
        setDay(e.currentTarget.value);
        break;
      case `${id}-mm`:
        setMonth(e.currentTarget.value);
        break;
      case `${id}-yyyy`:
        setYear(e.currentTarget.value);
        break;
    }
  };
  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    if (value.length === 1) {
      switch (event.currentTarget.id) {
        case `${id}-dd`:
          setDay(`0${value}`);
          break;
        case `${id}-mm`:
          setMonth(`0${value}`);
          break;
      }
    }
  };
  return (
    <FormControl id={id} {...props}>
      {props.label && (
        <FormLabel id={id} mb={1}>
          {props.label}
        </FormLabel>
      )}
      <HStack alignItems="start">
        {' '}
        <Input
          id={`${id}-dd`}
          w={13}
          value={day}
          variant="secondary"
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="DD"
          maxLength={2}
          type="text"
          aria-labelledby={`${id} date`}
          {...props}
        />
        <Input
          id={`${id}-mm`}
          w={15}
          value={month}
          variant="secondary"
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="MM"
          type="text"
          maxLength={2}
          aria-labelledby={`${id} month`}
          {...props}
        />
        <Input
          id={`${id}-yyyy`}
          w={17}
          value={year}
          variant="secondary"
          onChange={handleChange}
          maxLength={4}
          placeholder="YYYY"
          type="text"
          aria-labelledby={`${id} year`}
          {...props}
        />
        <Spacer />
      </HStack>
      {helper && <FormHelperText>{helper}</FormHelperText>}
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
});
